"use client";

import { Mail, TriangleAlert } from "lucide-react";
import { useId } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";

import { AuthFormFooter } from "@/app/(discover)/components/auth/auth-form-footer";
import { AuthMethodPanel } from "@/components/auth/auth-method-panel";
import { ButtonCountdown } from "@/components/auth/button-countdown";
import { formMode } from "@/components/auth/form-mode";
import { SharedDeviceChoice } from "@/components/auth/shared-device-choice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AUTH_AUDIENCES,
  type AuthAudience,
} from "@/lib/constants/auth-audiences";
import { authTestIds } from "@/lib/data-test-ids/components.data-test-ids";
import { isRetryCountdownActive } from "@/lib/errors/otp-throttle-error";
import { isInstitutionalEmailInput } from "@/lib/schemas/email";

export interface LoginFormData {
  email: string;
  sharedDevice: boolean;
}

interface LoginFormViewProps {
  form: UseFormReturn<LoginFormData>;
  isLoading: boolean;
  mode: formMode;
  audience: AuthAudience;
  onSubmit: (event: React.FormEvent) => void;
  onBack: () => void;
  retryCountdownSeconds: number | null;
  footer?: React.ReactNode;
}

const JOB_SEEKER_COPY = {
  emailLabel: "Email address",
  emailPlaceholder: "jane@example.com",
} as const;

const FORM_MODE_CONFIG = {
  [formMode.signin]: {
    ...JOB_SEEKER_COPY,
    heading: "Sign in with email",
    helperText:
      "We'll email you a 6-digit code to sign in. No password needed.",
    emailHint: undefined,
    buttonText: "Send me a code",
    loadingText: "Signing in...",
  },
  [formMode.signup]: {
    ...JOB_SEEKER_COPY,
    heading: "Sign up with email",
    helperText:
      "We'll email you a 6-digit code to confirm it's you. No password to remember.",
    emailHint: "Use a personal address, you keep it if you change schools.",
    buttonText: "Create job seeker account",
    loadingText: "Creating account...",
  },
} as const;

const DISTRICT_STAFF_FORM_COPY = {
  ...FORM_MODE_CONFIG[formMode.signin],
  emailLabel: "Work email address",
  emailPlaceholder: "you@district.org",
  helperText:
    "We'll email a 6-digit code to your work address. No password needed.",
} as const;

function EmailHint({
  isWarning,
  children,
}: {
  isWarning: boolean;
  children: React.ReactNode;
}) {
  if (isWarning) {
    return (
      <p
        className="flex items-start gap-1.5 text-xs text-warning"
        data-testid={authTestIds.INSTITUTIONAL_EMAIL_WARNING}
      >
        <TriangleAlert className="size-3.5 shrink-0 mt-px" />
        {children}
      </p>
    );
  }

  return <p className="text-xs text-ink-subtle">{children}</p>;
}

export default function LoginFormView({
  form,
  isLoading,
  mode,
  audience,
  onSubmit,
  onBack,
  retryCountdownSeconds,
  footer,
}: LoginFormViewProps) {
  const { watch } = form;
  const email = watch("email");
  const isDistrictStaff = audience === AUTH_AUDIENCES.DISTRICT_STAFF;
  const modeConfig = isDistrictStaff
    ? DISTRICT_STAFF_FORM_COPY
    : FORM_MODE_CONFIG[mode];
  const isRetryActive = isRetryCountdownActive(retryCountdownSeconds);
  const emailInputId = useId();
  const showInstitutionalWarning = isInstitutionalEmailInput(email);

  return (
    <AuthMethodPanel
      heading={modeConfig.heading}
      helperText={modeConfig.helperText}
      isLoading={isLoading}
      onBack={onBack}
      footer={
        <AuthFormFooter showLegalLinks={!isDistrictStaff}>
          {footer}
        </AuthFormFooter>
      }
    >
      <form onSubmit={onSubmit} className="space-y-3 sm:space-y-4">
        <div className="space-y-2">
          <Controller
            name="email"
            control={form.control}
            render={({ field, fieldState }) => (
              <div className="space-y-1.5">
                <Label htmlFor={emailInputId}>{modeConfig.emailLabel}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-ink-faint" />
                  <Input
                    {...field}
                    id={emailInputId}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder={modeConfig.emailPlaceholder}
                    className="pl-10 h-10 sm:h-11 text-base md:text-sm border-border focus:border-secondary focus:ring-2 focus:ring-secondary/20 rounded-lg"
                    disabled={isLoading}
                    autoFocus
                    data-testid={authTestIds.EMAIL_INPUT}
                  />
                </div>
                {fieldState.error ? (
                  <p className="text-destructive text-xs">
                    {fieldState.error.message}
                  </p>
                ) : (
                  modeConfig.emailHint && (
                    <EmailHint isWarning={showInstitutionalWarning}>
                      {modeConfig.emailHint}
                    </EmailHint>
                  )
                )}
              </div>
            )}
          />
        </div>

        <Button
          type="submit"
          disabled={!email || isLoading || isRetryActive}
          className="w-full h-10 sm:h-11 text-sm font-semibold !bg-secondary hover:!bg-secondary/90 text-primary-foreground transition-all shadow-sm hover:shadow-md rounded-lg"
          data-testid={authTestIds.SUBMIT_BUTTON}
        >
          <ButtonCountdown isLoading={isLoading} isRetryActive={isRetryActive}>
            <ButtonCountdown.Loading>
              {modeConfig.loadingText}
            </ButtonCountdown.Loading>
            <ButtonCountdown.Retry>Retry</ButtonCountdown.Retry>
            <ButtonCountdown.Default>
              {modeConfig.buttonText}
            </ButtonCountdown.Default>
          </ButtonCountdown>
        </Button>
        {isRetryActive && (
          <p className="text-xs text-ink-subtle text-center">
            You have requested too many codes. Please wait{" "}
            {retryCountdownSeconds} second
            {retryCountdownSeconds !== 1 ? "s" : ""} before trying again.
          </p>
        )}
      </form>
      <Controller
        name="sharedDevice"
        control={form.control}
        render={({ field }) => (
          <SharedDeviceChoice checked={field.value} onChange={field.onChange} />
        )}
      />
    </AuthMethodPanel>
  );
}
